/**
 * Every rule about which leagues are pinned and in what order, so pin, unpin
 * and reorder behave identically everywhere and are testable with no
 * renderer.
 *
 * Operates on the STORED array, unfiltered. It must never drop an id
 * because the catalog did not return it. Filtering for display happens only
 * in the view models (leagueSelectorRowModel.ts, leagueMenuViewModel.ts).
 */
import {
  MAX_PINNED_LEAGUES,
  type typeMoveDirection,
} from "./leagueSelectorContract";

/** Pin a league that is not pinned, or unpin one that is.
 *  A newly pinned league goes to the END of the list, so the fan's existing
 *  order is never disturbed. Returns a new array; never mutates the input. */
export function togglePinnedLeague(input: {
  pinnedLeagueIds: string[];
  leagueId: string;
}): string[] {
  const { pinnedLeagueIds, leagueId } = input;
  if (pinnedLeagueIds.includes(leagueId)) {
    return pinnedLeagueIds.filter((id) => id !== leagueId);
  }
  if (pinnedLeagueIds.length >= MAX_PINNED_LEAGUES) {
    return [...pinnedLeagueIds];
  }
  return [...pinnedLeagueIds, leagueId];
}

/** A move the sheet can apply: put `leagueId` where `targetLeagueId` is.
 *  `position` is the moved league's 1-based place in the VISIBLE list once
 *  it lands (ISSUE-977). */
export type typePinnedLeagueMove = {
  leagueId: string;
  targetLeagueId: string;
  position: number;
};

/** Moves `leagueId` to the STORED index `targetLeagueId` holds. The stored
 *  list can hold ids the catalog does not return (see the file header), so
 *  this indexes into the stored list, never the visible one (ISSUE-977).
 *  A no-op when either id is absent or they are the same. Returns a new
 *  array; never mutates the input. */
export function movePinnedLeagueTo(input: {
  pinnedLeagueIds: string[];
  leagueId: string;
  targetLeagueId: string;
}): string[] {
  const { pinnedLeagueIds, leagueId, targetLeagueId } = input;
  const from = pinnedLeagueIds.indexOf(leagueId);
  const to = pinnedLeagueIds.indexOf(targetLeagueId);
  if (from === -1 || to === -1 || from === to) {
    return [...pinnedLeagueIds];
  }
  const next = [...pinnedLeagueIds];
  next.splice(from, 1);
  next.splice(to, 0, leagueId);
  return next;
}

/** Turns a drop on another league into a move to its place, over the
 *  VISIBLE list a fan sees. `null` when the drop lands on no league, on
 *  itself, or on a league the visible list does not hold (ISSUE-977). */
export function resolvePinnedLeagueDrop(input: {
  visibleLeagueIds: string[];
  activeLeagueId: string;
  overLeagueId: string | null;
}): typePinnedLeagueMove | null {
  const { visibleLeagueIds, activeLeagueId, overLeagueId } = input;
  if (
    overLeagueId === null ||
    overLeagueId === activeLeagueId ||
    !visibleLeagueIds.includes(activeLeagueId) ||
    !visibleLeagueIds.includes(overLeagueId)
  ) {
    return null;
  }
  return {
    leagueId: activeLeagueId,
    targetLeagueId: overLeagueId,
    position: visibleLeagueIds.indexOf(overLeagueId) + 1,
  };
}

/** Turns an arrow key press into a move one place past the league above
 *  ("up") or below ("down") in the VISIBLE list. `null` when the league is
 *  absent from the list or already at that end (ISSUE-977). */
export function resolvePinnedLeagueStep(input: {
  visibleLeagueIds: string[];
  leagueId: string;
  direction: typeMoveDirection;
}): typePinnedLeagueMove | null {
  const { visibleLeagueIds, leagueId, direction } = input;
  const index = visibleLeagueIds.indexOf(leagueId);
  if (index === -1) {
    return null;
  }
  if (direction === "up" && index === 0) {
    return null;
  }
  if (direction === "down" && index === visibleLeagueIds.length - 1) {
    return null;
  }
  const targetIndex = direction === "up" ? index - 1 : index + 1;
  // `targetIndex` is known in-bounds from the guards above, but
  // `noUncheckedIndexedAccess` still types the read as `string | undefined`.
  // `leagueId` is a fallback that can never actually be used here.
  const targetLeagueId = visibleLeagueIds[targetIndex] ?? leagueId;
  const position = direction === "up" ? index : index + 2;
  return { leagueId, targetLeagueId, position };
}

/** Reads the dragged and target league ids off a dnd-kit drop event and
 *  calls resolvePinnedLeagueDrop. Kept separate so the sheet's drop handler
 *  is one line, and this pure rule stays unit tested (ISSUE-977). The
 *  `event` type is structural so this file imports nothing from
 *  `@dnd-kit`; dnd-kit's `DragEndEvent` is assignable to it. */
export function resolvePinnedLeagueDragEnd(input: {
  visibleLeagueIds: string[];
  event: {
    active: { id: string | number };
    over: { id: string | number } | null;
  };
}): typePinnedLeagueMove | null {
  return resolvePinnedLeagueDrop({
    visibleLeagueIds: input.visibleLeagueIds,
    activeLeagueId: String(input.event.active.id),
    overLeagueId:
      input.event.over === null ? null : String(input.event.over.id),
  });
}
